// Components
import { ScreenReaderOnly } from "../../screenReaderOnly/ScreenReaderOnly";
import { AtMentionPicker } from "../atMentionPicker/AtMentionPicker";
// Plugins
import { ContentEdit, HyperLink, Paste, Watermark } from "roosterjs-editor-plugins";
import { Rooster } from "roosterjs-react/lib-mjs";
import { RoosterBlockDnDPlugin } from "../richTextEditor/plugins/RoosterBlockDnDPlugin";
import { RoosterBlockImagePastePlugin } from "../richTextEditor/plugins/RoosterBlockImagePastePlugin";
import { AtMentionRoosterPlugin } from "./plugins/AtMentionRoosterPlugin";
import { KeyboardEventPlugin } from "./plugins/KeyboardEventPlugin";
// Constants
import { AtMentionPickerListId, PersonMentionSchema } from "@ms/uno-constants/lib/local/ChatConstants";
import { Colors } from "@ms/uno-constants/lib/local/Colors";
import { Key } from "@ms/uno-constants/lib/local/KeyCode";
// Styles
import { useChatMessageEditorStyles } from "./ChatMessageEditor.styles";
// Utilities
import { getRTL } from "@fluentui/utilities/lib/rtl";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { sanitizeForTrustedTypes } from "@ms/uno-utilities/lib/local/ContentSanitizationUtilities";
import React, { useRef } from "react";
import { useLoggers } from "../../../hooks/useLoggers";
import { getMentionUserListItemId, getSuggestedUsersForMentionPicker } from "../../../utilities/ChatAtMentionsUtilities";
const defaultAtMentionsState = {
    isPickerOpen: false,
    targetElement: null,
    mentionableUsers: [],
    highlightedIndex: 0
};
const DefaultPlaceholderFormat = {
    textColor: Colors.Grey46
};
const NumberOfMentionSuggestions = 5;
/**
 * Rich text chat message editor based on RoosterJS
 */ export function ChatMessageEditor({ getMentionableUsers, onOpenAtMentionPicker, isEditorRibbonShown, onSendMessage, toggleFormattingRibbon, getUserPhoto, onRenderPersona, recentMessages, prioritizedUserIdsForMentionSuggestions, plugins, atMentionPickerMountNode, focusOnInit, placeholderFormat }) {
    const { traceLogger } = useLoggers();
    // Add ref to track current isEditorRibbonShown value
    const isEditorRibbonShownRef = useRef(isEditorRibbonShown);
    // Update ref when prop changes
    React.useEffect(()=>{
        isEditorRibbonShownRef.current = isEditorRibbonShown;
    }, [
        isEditorRibbonShown
    ]);
    //#region Keyboard Event Handlers
    const handleEnterKey = (event, editor)=>{
        const isPickerOpen = atMentionsPickerControlsRef.current?.isAtMentionPickerOpen ?? false;
        const isRibbonShown = isEditorRibbonShownRef.current;
        // Define send conditions based on ribbon state
        const shouldSendMessage = isRibbonShown ? event.ctrlKey && !event.shiftKey && !event.altKey && !event.metaKey // Ribbon shown: only Ctrl+Enter
         : !event.shiftKey && !event.altKey && !event.metaKey; // Ribbon hidden: Enter or Ctrl+Enter
        if (!isPickerOpen && shouldSendMessage) {
            event.preventDefault();
            event.stopPropagation();
            onSendMessage();
        }
    };
    const handleCtrlShiftX = (event, editor)=>{
        // Toggle the editor ribbon visibility
        toggleFormattingRibbon();
    };
    //#endregion
    //#region Rooster Plugins
    const pluginsInitialized = useRef(false);
    const roosterPlugins = useRef([]);
    const keyboardEventPluginRef = useRef(null);
    const atMentionPluginRef = useRef(null);
    if (!pluginsInitialized.current) {
        if (plugins.length > 0) {
            roosterPlugins.current.push(...plugins);
        }
        const keyboardEventHandler = [
            {
                key: Key.ENTER,
                handler: handleEnterKey
            },
            {
                key: "X",
                ctrlKey: true,
                shiftKey: true,
                handler: handleCtrlShiftX
            }
        ];
        // Add KeyboardEventPlugin if there are any handlers
        keyboardEventPluginRef.current = new KeyboardEventPlugin(keyboardEventHandler);
        roosterPlugins.current.push(keyboardEventPluginRef.current);
        atMentionPluginRef.current = new AtMentionRoosterPlugin(()=>atMentionsPickerControlsRef.current);
        roosterPlugins.current.push(atMentionPluginRef.current);
        // Watermark plugin controls rendering the placeholder with WCAG AA compliant color
        const watermarkFormat = placeholderFormat ?? DefaultPlaceholderFormat;
        roosterPlugins.current.push(new Watermark(Strings.TaskChat_MessagePlaceholder, watermarkFormat));
        // Hyperlink plugin handles links in the content
        // See https://github.com/microsoft/roosterjs/wiki/HyperLink-plugin#parameters for more details.
        roosterPlugins.current.push(new HyperLink(/** getTooltipCallback */ undefined, /** target */ "_blank", /** onLinkClick */ (anchor, event)=>{
            // Currently  links on simple click in RTE do not open. Only Ctrl + Click.
            // This code was proposed by roosterJS team to address that issue as they had the same issue with the demo:
            // https://github.com/microsoft/roosterjs/pull/1460/files
            try {
                const href = anchor?.href;
                if (href && event.button === 0) {
                    event.preventDefault();
                    safelyOpenExternalUrl(href, traceLogger, "_blank");
                    return true;
                }
            } catch  {
                return false;
            }
            return false;
        }));
        // Paste plugin, handles BeforePaste event and reformat some special content, including:
        // 1. Content copied from Word
        // 2. Content copied from Excel
        // 3. Content copied from Word Online or Onenote Online
        roosterPlugins.current.push(new Paste());
        // Content Edit plugin improves content editing interactions
        roosterPlugins.current.push(new ContentEdit());
        // Plugins that block pasting images and drag and drop
        roosterPlugins.current.push(new RoosterBlockImagePastePlugin());
        roosterPlugins.current.push(new RoosterBlockDnDPlugin());
        pluginsInitialized.current = true;
    }
    //#endregion
    //#region At Mention Picker
    const [atMentionsState, setAtMentionsState] = React.useState(defaultAtMentionsState);
    const setAtMentionFilterText = (filterText)=>{
        setAtMentionsState((prevState)=>({
                ...prevState,
                mentionableUsers: getSuggestedUsersForMentionPicker(getMentionableUsers(), recentMessages, prioritizedUserIdsForMentionSuggestions, filterText, NumberOfMentionSuggestions),
                highlightedIndex: 0
            }));
    };
    const showAtMentionPicker = (target)=>{
        onOpenAtMentionPicker();
        setAtMentionsState((prevState)=>({
                ...prevState,
                targetElement: target,
                isPickerOpen: true,
                mentionableUsers: getSuggestedUsersForMentionPicker(getMentionableUsers(), recentMessages, prioritizedUserIdsForMentionSuggestions, "", NumberOfMentionSuggestions),
                highlightedIndex: 0
            }));
    };
    const hideAtMentionPicker = ()=>{
        setAtMentionsState(defaultAtMentionsState);
    };
    const highlightPreviousUser = ()=>{
        setAtMentionsState((prevState)=>{
            const newIndex = prevState.highlightedIndex > 0 ? prevState.highlightedIndex - 1 : 0;
            return {
                ...prevState,
                highlightedIndex: newIndex
            };
        });
    };
    const highlightNextUser = ()=>{
        setAtMentionsState((prevState)=>{
            const newIndex = prevState.highlightedIndex < prevState.mentionableUsers.length - 1 ? prevState.highlightedIndex + 1 : prevState.mentionableUsers.length - 1;
            return {
                ...prevState,
                highlightedIndex: newIndex
            };
        });
    };
    const onAtMentionUserSelected = (user)=>{
        atMentionPluginRef.current?.insertAtMentionUser(user.id, user.displayName, PersonMentionSchema);
        hideAtMentionPicker();
    };
    const insertHighlightedUser = ()=>{
        const { isPickerOpen, mentionableUsers, highlightedIndex } = atMentionsState;
        if (!isPickerOpen || highlightedIndex < 0 || highlightedIndex >= mentionableUsers.length) {
            return;
        }
        const user = mentionableUsers[highlightedIndex];
        if (user) {
            onAtMentionUserSelected(user);
        }
    };
    // This ref lets AtMentionRoosterPlugin access the latest values of the at-mention picker controls
    const atMentionsPickerControlsRef = useRef({
        isAtMentionPickerOpen: atMentionsState.isPickerOpen,
        setAtMentionFilterText,
        showAtMentionPicker,
        hideAtMentionPicker,
        highlightPreviousUser,
        highlightNextUser,
        insertHighlightedUser
    });
    atMentionsPickerControlsRef.current = {
        isAtMentionPickerOpen: atMentionsState.isPickerOpen,
        setAtMentionFilterText,
        showAtMentionPicker,
        hideAtMentionPicker,
        highlightPreviousUser,
        highlightNextUser,
        insertHighlightedUser
    };
    const mentionStatusId = "mention-picker-status";
    const renderAtMentionPicker = ()=>{
        const { isPickerOpen, targetElement, mentionableUsers, highlightedIndex } = atMentionsState;
        if (!isPickerOpen || !targetElement) {
            return null;
        }
        const statusMessage = mentionableUsers.length === 0 ? Strings.TaskChat_AtMentionPickerNoSuggestions : "";
        return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(AtMentionPicker, {
            targetElement: targetElement,
            mountNode: atMentionPickerMountNode,
            mentionableUsers: mentionableUsers,
            highlightedItemIndex: highlightedIndex,
            onUserSelected: onAtMentionUserSelected,
            getUserPhoto: getUserPhoto,
            onRenderPersona: onRenderPersona
        }), /*#__PURE__*/ React.createElement(ScreenReaderOnly, null, /*#__PURE__*/ React.createElement("div", {
            id: mentionStatusId,
            role: "status",
            "aria-live": "polite",
            "aria-atomic": "true"
        }, statusMessage)));
    };
    const atMentionsAriaProps = atMentionsState.isPickerOpen ? {
        "aria-expanded": true,
        "aria-controls": AtMentionPickerListId,
        "aria-autocomplete": "list",
        "aria-activedescendant": getMentionUserListItemId(atMentionsState.highlightedIndex)
    } : {};
    //#endregion
    const styles = useChatMessageEditorStyles();
    // Preserve HTML comments as RoosterJS internally rely on it for cut/copy operations, this is not needed in newer versions of RoosterJS
    // See https://github.com/microsoft/roosterjs/issues/2289
    const trustedHTMLHandler = (content)=>sanitizeForTrustedTypes(content, {
            allowedTags: [
                "#comment"
            ]
        });
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Rooster, {
        className: styles.root,
        dir: getRTL() ? "rtl" : "ltr",
        plugins: roosterPlugins.current,
        role: "textbox",
        "aria-placeholder": Strings.TaskChat_MessagePlaceholder,
        focusOnInit: focusOnInit,
        trustedHTMLHandler: trustedHTMLHandler,
        ...atMentionsAriaProps
    }), renderAtMentionPicker());
}
